<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useUiStore } from '@/stores/ui'

/**
 * The toast stack.
 *
 * A polite live region, so a success announcement does not interrupt whatever a screen reader is
 * in the middle of — except that errors are `assertive`, because an operation that failed on a
 * client site is worth interrupting for.
 */
const ui = useUiStore()
const { t } = useI18n()
</script>

<template>
  <div class="host" aria-live="polite">
    <TransitionGroup name="toast">
      <div
        v-for="toast in ui.toasts"
        :key="toast.id"
        class="toast"
        :data-state="toast.kind === 'success' ? 'ok' : toast.kind"
        :role="toast.kind === 'error' ? 'alert' : 'status'"
      >
        <div class="text">
          <p class="title">{{ toast.title }}</p>
          <p v-if="toast.detail" class="detail">{{ toast.detail }}</p>
        </div>
        <button
          type="button"
          :aria-label="t('ui.dismissToast')"
          @click="ui.dismissToast(toast.id)"
        >
          <svg viewBox="0 0 12 12" aria-hidden="true">
            <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" stroke-width="1.5" fill="none" />
          </svg>
        </button>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.host {
  position: fixed;
  right: var(--space-4);
  bottom: var(--space-4);
  z-index: var(--z-toast);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  width: min(24rem, calc(100vw - var(--space-8)));
  pointer-events: none;
}

.toast {
  pointer-events: auto;
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-3);
  border-radius: var(--radius-md);
  border: var(--border-width) solid var(--state-border);
  border-left-width: 3px;
  background: var(--bg-surface-raised);
  box-shadow: var(--shadow-md);
}

.text {
  flex: 1;
  min-width: 0;
}

.title {
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--text-strong);
}

.detail {
  font-size: var(--text-xs);
  color: var(--text-muted);
  margin-top: var(--space-1);
  overflow-wrap: anywhere;
}

button {
  background: none;
  border: 0;
  color: var(--text-subtle);
  cursor: pointer;
  padding: var(--space-1);
  flex: none;
  border-radius: var(--radius-sm);
}

button:hover {
  color: var(--text-strong);
  background: var(--bg-hover);
}

button svg {
  width: 0.75rem;
  height: 0.75rem;
}

.toast-enter-active,
.toast-leave-active {
  transition:
    opacity var(--duration-base) var(--ease-out),
    transform var(--duration-base) var(--ease-out);
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(0.75rem);
}
</style>
